import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useToggle } from '@mantine/hooks';

import { TrackableList, TrackableObjectType } from 'types/trackables';
import { OffsetPaginationParams } from 'types/pagination';

import {
  useTrackableLists,
  UseTrackableListsParams,
} from '../../hooks/useTrackableLists';
import { useTagsFilterParams } from 'tags/hooks/useTagsFilterParams';
import { TRACKABLE_OBJECT_TYPE_OPTIONS } from '../../utils';

import { ExternalLink } from 'components/ExternalLink/ExternalLink';
import * as PageLayout from 'components/PageLayout';
import { QuerySearch } from 'components/QuerySearch/QuerySearch';
import { Select } from 'components/Select/Select';

import { TrackableListsTable } from '../../components/TrackableListsTable/TrackableListsTable';
import { AddTrackableListDialog } from '../../components/AddTrackableListDialog';

export function TrackableListsOverview() {
  const navigate = useNavigate();
  const [isCreatingList, toggleIsCreatingList] = useToggle();

  const tagFilters = useTagsFilterParams();
  const [queryFilters, setQueryFilters] = React.useState<
    UseTrackableListsParams & OffsetPaginationParams
  >({
    page: 1,
  });

  const queryResult = useTrackableLists({
    params: {
      ...tagFilters,
      ...queryFilters,
    },
    keepPreviousData: true,
  });

  const [searchFilters, setSearchFilters] = React.useState<
    UseTrackableListsParams & OffsetPaginationParams
  >({});

  const searchQueryResult = useTrackableLists({
    params: {
      ...tagFilters,
      ...searchFilters,
      limit: 20,
    },
    enabled: Object.keys(searchFilters).length > 0,
    keepPreviousData: false,
  });

  const handleSearchInput = React.useCallback((value?: string) => {
    if (!value) {
      setSearchFilters({});
      setQueryFilters({ page: 1 });
      return;
    }

    setSearchFilters({
      trackable_list_names: [value],
      page: 1,
    });
  }, []);

  function handleApplyFilters() {
    setQueryFilters({ ...searchFilters, page: 1 });
  }

  function handleSuggestionSelect({ id }: TrackableList) {
    navigate(id);
  }

  function handleObjectTypeFilterChange(objectType: string | null) {
    setQueryFilters((previousFilters) => ({
      ...previousFilters,
      object_types: objectType
        ? [objectType as TrackableObjectType]
        : undefined,
      page: 1,
    }));

    setSearchFilters((previousFilters) => ({
      ...previousFilters,
      object_types: objectType
        ? [objectType as TrackableObjectType]
        : undefined,
    }));
  }

  const hasFilters =
    Object.keys(queryFilters).length > 0 ||
    Object.values(tagFilters).some(Boolean);

  return (
    <PageLayout.Root>
      <QuerySearch
        entityName="trackable list"
        searchLabel="Search watchlists..."
        searchResultField="name"
        searchQueryResult={searchQueryResult}
        onApplyFilters={handleApplyFilters}
        onSuggestionSelect={handleSuggestionSelect}
        onValueChange={handleSearchInput}
        totalElements={queryResult.data?.total_elements}
        filters={
          <>
            <Select
              onChange={(option) =>
                handleObjectTypeFilterChange(option?.value || null)
              }
              options={TRACKABLE_OBJECT_TYPE_OPTIONS}
              placeholder="Object type"
              size="small"
              isClearable
            />
            <div className="flex items-center gap-2 pl-2">
              <ExternalLink href="https://docs.lumeo.com/docs/trackables">
                Watchlists documentation
              </ExternalLink>
            </div>
          </>
        }
      />

      <TrackableListsTable
        queryResult={queryResult}
        pageSize={50}
        page={queryFilters.page}
        onPageChange={(page) =>
          setQueryFilters((previous) => ({ ...previous, page }))
        }
        hasFilters={hasFilters}
        onCreate={toggleIsCreatingList}
      />

      <AddTrackableListDialog
        open={isCreatingList}
        onClose={toggleIsCreatingList}
      />
    </PageLayout.Root>
  );
}
